import axiosRetry from 'axios-retry';
import axios, { AxiosError, AxiosResponse } from 'axios';

import { useAuthStore } from 'src/auth/auth.store';
import { PATH_TO_REFRESH_TOKENS } from 'src/config-global';

import { HttpTypes } from './types.http';
import { AxiosConnectionError } from './errors';
import { setAxiosAuthorization } from './axios-authorization-header';

export const api = axios.create({
  timeout: 60000,
  headers: {
    'Content-Type': 'application/json',
  },
});

axiosRetry(api, { retries: 3 });


export class AxiosHttpClient implements HttpTypes.HttpClient {
  private readonly url: string = '';

  constructor(
    private readonly baseURL: string,
    private readonly interceptors?: any
  ) {
    const auth = useAuthStore.getState();
    if (auth.accessToken && api.defaults.headers.Authorization !== `Bearer ${auth.accessToken}`) {
      setAxiosAuthorization(api, auth.accessToken);
    }

    if (this.baseURL) {
      this.url = this.baseURL;
    }

    if (interceptors) {
      this.interceptors?.forEach((interceptor: ['request' | 'response', ...any[]]) => {
        const [type, ...args] = interceptor;
        api.interceptors[type].use(...args);
      });
    }
  }

  async getTokens(refreshTokenParam?: string): Promise<void> {
    let auth = useAuthStore.getState();
    const data: HttpTypes.HttpRequest = {
      url: PATH_TO_REFRESH_TOKENS(auth.user?.id),
      method: 'post',
      body: {
        refreshToken: refreshTokenParam
      }
    };
    const response = await this.request(data, false);
    const { access_token, refresh_token, user } = response.body as any;
    auth = useAuthStore.getState();
    if (access_token && refresh_token && user) {
      auth.setSession(access_token, refresh_token, user); // Em todo projeto Verificar o objeto que é retornado do back pra passar certo o valor
      setAxiosAuthorization(api, access_token);
    } else {
      auth.clearSession();
    }
  }

  async request(
    data: HttpTypes.HttpRequest,
    retryToGetTokens: boolean = true
  ): Promise<HttpTypes.HttpResponse> {
    let axiosResponse: AxiosResponse;
    let auth = useAuthStore.getState();

    try {
      axiosResponse = await api.request({
        url: `${this.url}/${data.url}`,
        method: data.method,
        data: data.body,
        params: data.params,
        responseType: data.responseType,
        headers: {
          ...data.headers,
        },
      });
    } catch (error: any) {
      const errors = error as AxiosError;
      if (error?.response?.status === 401 && auth.refreshToken && retryToGetTokens) {
        try {
          await this.getTokens(auth.refreshToken);
           auth = useAuthStore.getState();
          if (auth.isAuthenticated) {
            return await this.request(data, false);
          }
        } catch (refereshError: any) {
          console.log('[ERROR]', refereshError.message);
        }
      }

      if (!retryToGetTokens) {
        auth.clearSession();
      }

      if (errors.response) {
        axiosResponse = errors?.response;
      } else if (errors.request) {
        axiosResponse = errors?.request;
      } else {
        axiosResponse = AxiosConnectionError as AxiosResponse;
      }
    }
    return {
      statusCode: axiosResponse.status,
      body: axiosResponse.data,
    };
  }

  // eslint-disable-next-line class-methods-use-this
  async fakeRequest(
    data: HttpTypes.HttpRequest,
    options: HttpTypes.RequestOptions
  ): Promise<HttpTypes.HttpResponse> {
    const fakeResponse: HttpTypes.HttpResponse = {
      body: options.data,
      statusCode: options.status,
    };

    if (options.delay) {
      await new Promise((resolve) => setTimeout(resolve, options.delay));
    }

    return Promise.resolve(fakeResponse);
  }
}
